"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const bentoItems = [
  {
    id: "b1",
    type: "stat",
    value: "50,000+",
    label: "Happy Children",
    sub: "across 30+ countries",
    icon: "FaceSmileIcon",
    bg: "bg-plum",
    textColor: "text-white",
    span: "col-span-1 md:col-span-2 row-span-1",
  },
  {
    id: "b2",
    type: "feature",
    icon: "AcademicCapIcon",
    title: "Educator Approved",
    desc: "Developed with child development experts and Montessori-trained educators.",
    bg: "bg-white",
    textColor: "text-plum",
    span: "col-span-1 row-span-1",
  },
  {
    id: "b3",
    type: "feature",
    icon: "PaintBrushIcon",
    title: "Premium Paper Quality",
    desc: "Thick, bleed-proof pages perfect for crayons, markers, and watercolors.",
    bg: "bg-peach/20",
    textColor: "text-plum",
    span: "col-span-1 row-span-1",
  },
  {
    id: "b4",
    type: "big-feature",
    icon: "GlobeAltIcon",
    title: "Ships Worldwide",
    desc: "Fast delivery to USA, UK, Singapore, Australia, Norway, Switzerland, and 25+ more countries.",
    bg: "bg-mint/30",
    textColor: "text-plum",
    span: "col-span-1 md:col-span-2 row-span-1",
    emoji: "🌍",
  },
  {
    id: "b5",
    type: "stat",
    value: "4.9 / 5",
    label: "Average Rating",
    sub: "from 5,000+ reviews",
    icon: "StarIcon",
    bg: "bg-gold",
    textColor: "text-plum",
    span: "col-span-1 row-span-1",
  },
  {
    id: "b6",
    type: "feature",
    icon: "SparklesIcon",
    title: "Bilingual Options",
    desc: "Select titles available in English, French, Spanish, and Arabic.",
    bg: "bg-lavender/30",
    textColor: "text-plum",
    span: "col-span-1 row-span-1",
  },
  {
    id: "b7",
    type: "feature",
    icon: "ShieldCheckIcon",
    title: "Safe & Non-Toxic",
    desc: "All materials are child-safe, non-toxic, and eco-friendly certified.",
    bg: "bg-green-soft/40",
    textColor: "text-plum",
    span: "col-span-1 row-span-1",
  },
  {
    id: "b8",
    type: "stat",
    value: "30+",
    label: "Book Titles",
    sub: "New arrivals every season",
    icon: "BookOpenIcon",
    bg: "bg-plum-100",
    textColor: "text-plum",
    span: "col-span-1 row-span-1",
  },
];

export default function WhyUsSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.remove("hidden-before-reveal");
        });
      },
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".reveal").forEach((el) => {
      el.classList.add("hidden-before-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 section-dreamy dots-bg">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="reveal text-center mb-14">
          <span className="text-xs font-black tracking-widest uppercase text-plum-light bg-white px-3 py-1 rounded-full shadow-dreamy">
            💜 Why Families Love Us
          </span>
          <h2 className="font-fraunces text-4xl md:text-5xl font-black text-plum mt-4 leading-tight">
            More Than Just
            <span className="italic text-plum-light"> Coloring Books</span>
          </h2>
          <p className="text-plum/60 mt-3 max-w-xl mx-auto text-base">
            Every book is a doorway to imagination, learning, and joy — trusted by parents in 30+ countries.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[160px]">
          {bentoItems.map((item, i) => (
            <div
              key={item.id}
              className={`reveal ${item.span} ${item.bg} rounded-3xl p-6 shadow-dreamy flex flex-col justify-between overflow-hidden relative group`}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              {item.type === "stat" ? (
                <>
                  <Icon name={item.icon as any} size={28} className={`${item.textColor} opacity-60`} />
                  <div>
                    <div className={`font-fraunces text-3xl md:text-4xl font-black ${item.textColor} leading-none`}>
                      {item.value}
                    </div>
                    <div className={`font-bold text-sm mt-1 ${item.textColor}`}>{item.label}</div>
                    <div className={`text-xs mt-0.5 ${item.textColor} opacity-60`}>{item.sub}</div>
                  </div>
                </>
              ) : item.type === "big-feature" ? (
                <>
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{item.emoji}</div>
                    <div>
                      <h3 className={`font-fraunces font-bold text-lg ${item.textColor} leading-tight`}>{item.title}</h3>
                      <p className={`text-xs mt-1 ${item.textColor} opacity-70 leading-relaxed`}>{item.desc}</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`w-10 h-10 rounded-2xl bg-white/60 flex items-center justify-center`}>
                    <Icon name={item.icon as any} size={20} className="text-plum" />
                  </div>
                  <div>
                    <h3 className={`font-fraunces font-bold text-base ${item.textColor} leading-tight`}>{item.title}</h3>
                    <p className={`text-xs mt-1 ${item.textColor} opacity-65 leading-relaxed`}>{item.desc}</p>
                  </div>
                </>
              )}
              {/* Hover glow */}
              <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "radial-gradient(circle at 70% 30%, rgba(255,255,255,0.15), transparent 70%)" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}